import { useMemo } from "react";
import { useParams } from "react-router-dom";
import AdminOnly from "#/components/auth/AdminOnly";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import Card from "#/components/shared/card";
import Typography from "#/components/shared/typography";
import { buildMediaUrl } from "#/lib/mediaUrls";
import { useMediaDetail } from "#/features/media/hooks/useMediaDetail";

function formatBytes(bytes?: number | null) {
  if (!bytes || Number.isNaN(bytes)) {
    return "—";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleString();
}

export default function MediaDetailPage() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const { data, isLoading, isError, error, refetch } = useMediaDetail(
    idOrSlug
  );

  const imageUrl = useMemo(
    () => buildMediaUrl(data?.url ?? data?.slug ?? ""),
    [data]
  );

  if (isLoading) {
    return (
      <AdminOnly>
        <div className="container mx-auto px-4 py-8">
          <Typography variant="body1" className="text-brown-600">
            Loading media details…
          </Typography>
        </div>
      </AdminOnly>
    );
  }

  if (isError || !data) {
    return (
      <AdminOnly>
        <div className="container mx-auto px-4 py-8">
          <Typography variant="h4" className="text-terracotta">
            Unable to load media
          </Typography>
          <Typography variant="body2" className="text-brown-600">
            {error instanceof Error ? error.message : "Please try again."}
          </Typography>
          <div className="mt-4">
            <ActionButton tone="secondary" onClick={() => refetch()}>
              Retry
            </ActionButton>
          </div>
        </div>
      </AdminOnly>
    );
  }

  return (
    <AdminOnly>
      <div className="min-h-screen bg-cream-50">
        <div className="container mx-auto px-4 py-8 space-y-6">
          <div className="space-y-2">
            <Typography variant="h2" className="text-brown-900">
              {data.name ?? data.slug ?? "Media detail"}
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              {data.slug ?? data.url}
            </Typography>
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Card className="p-4">
              <div className="flex items-center justify-between gap-3">
                <Typography variant="subtitle1" className="text-brown-800">
                  Preview
                </Typography>
                {data.url ? (
                  <a
                    href={data.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-terracotta-600 underline underline-offset-2"
                  >
                    Open original
                  </a>
                ) : null}
              </div>
              <div className="mt-4 flex items-center justify-center rounded-2xl border border-cream-200 bg-white p-6">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={data.name ?? data.slug ?? "Media preview"}
                    className="max-h-[70vh] w-full object-contain"
                  />
                ) : (
                  <Typography variant="body2" className="text-brown-500">
                    No preview available.
                  </Typography>
                )}
              </div>
            </Card>

            <div className="space-y-4">
              <Card className="p-4 space-y-3">
                <Typography variant="subtitle1" className="text-brown-800">
                  Details
                </Typography>
                <div className="text-sm text-brown-700 space-y-3">
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      URL
                    </Typography>
                    {data.url ? (
                      <a
                        href={data.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block break-all text-sm text-terracotta-600 underline underline-offset-2"
                      >
                        {data.url}
                      </a>
                    ) : (
                      <Typography variant="body2">—</Typography>
                    )}
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Type
                    </Typography>
                    <Typography variant="body2">
                      {data.image_type ?? "—"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Subject
                    </Typography>
                    <Typography variant="body2">
                      {data.subject ?? "—"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Embedding
                    </Typography>
                    <Typography variant="body2">
                      {data.has_embedding === null ||
                      data.has_embedding === undefined
                        ? "—"
                        : data.has_embedding
                        ? "Yes"
                        : "No"}
                    </Typography>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Typography variant="caption" className="text-brown-500">
                        Size
                      </Typography>
                      <Typography variant="body2">
                        {formatBytes(data.size_bytes)}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="caption" className="text-brown-500">
                        Dimensions
                      </Typography>
                      <Typography variant="body2">
                        {data.width && data.height
                          ? `${data.width}×${data.height}`
                          : "—"}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Checksum
                    </Typography>
                    <Typography variant="body2" className="break-all">
                      {data.checksum ?? "—"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Source
                    </Typography>
                    <Typography variant="body2">
                      {data.source ?? "—"}
                    </Typography>
                  </div>
                </div>
              </Card>

              <Card className="p-4 space-y-3">
                <Typography variant="subtitle1" className="text-brown-800">
                  Metadata
                </Typography>
                <div className="text-sm text-brown-700 space-y-3">
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Prompt
                    </Typography>
                    <Typography variant="body2" className="break-words">
                      {data.prompt ?? "—"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Relevant text
                    </Typography>
                    <div className="max-h-40 overflow-auto rounded-md border border-cream-200 bg-cream-50 p-2">
                      <Typography
                        variant="body2"
                        className="whitespace-pre-wrap break-words"
                      >
                        {data.relevant_text ?? "—"}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Input URL
                    </Typography>
                    <Typography variant="body2" className="break-all">
                      {data.input_url ?? "—"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Input MIME
                    </Typography>
                    <Typography variant="body2">
                      {data.input_mime ?? "—"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Created by
                    </Typography>
                    <Typography variant="body2">
                      {data.created_by ?? "—"}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(data.created_at)}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" className="text-brown-500">
                      Updated
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(data.updated_at)}
                    </Typography>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AdminOnly>
  );
}
